<template>
  <CSelect
    :consistent-menu-width="false"
    :options="sortedOptions"
    :showCheckmark="false"
    :value="textType"
    @update:value="setText($event)"
  />
</template>

<script lang="ts" setup>
import type { Level } from '@tiptap/extension-heading'
import type { Editor } from '@tiptap/vue-3'
import { sortBy } from 'lodash-es'
import { computed, inject, type ComputedRef } from 'vue'

import CSelect from '@lib/components/CSelect/CSelect.vue'

import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import useSharedTranslations from '@lib/composables/useSharedTranslations'
import { styling as headingStyling } from '@lib/components/CRichTextEditor/extensions/headingExtended'
import { Paragraphs, styling as paragraphStyling } from '@lib/components/CRichTextEditor/extensions/paragraphExtended'
import { type CanvasTypographyPreset } from '@lib/types/canvasTheme'
import { textStylePresets } from '@theme/theme.utils'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

interface Props {
  editor: Editor
}

const { typographyPresetTranslations } = useSharedTranslations()
const props = defineProps<Props>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { activeCanvas } = useCanvas()
const { activeTheme } = useCanvasTheme()

const options = computed(() =>
  textStylePresets.map((option) => {
    const label = typographyPresetTranslations[option.id as keyof typeof typographyPresetTranslations]
    if (!launchDarkly.value.enable_canvas_tokens) return { ...option, label }

    const preset: CanvasTypographyPreset | undefined = activeCanvas.value?.theme?.typography?.[option.id]

    if (!!launchDarkly.value.enable_themes && activeTheme.value) {
      const preset = activeTheme.value.typography[option.id]

      return {
        ...option,
        label,
        class: 'letter-spacing--inherit',
        style: {
          color: preset.color,
          fontFamily: preset.font_family,
          fontSize: preset.font_size,
          fontWeight: preset.font_weight,
          letterSpacing: preset.letter_spacing,
          lineHeight: preset.line_height,
          textTransform: preset.text_transform,
        },
      }
    }

    if (!preset) {
      return { ...option, label }
    }

    return {
      ...option,
      label,
      class: 'letter-spacing--inherit',
      style: {
        color: preset.color,
        fontFamily: preset.font_family,
        fontSize: preset.font_size,
        fontWeight: preset.font_weight,
        letterSpacing: preset.letter_spacing,
        lineHeight: preset.line_height,
        textTransform: preset.text_transform,
      },
    }
  }),
)

const sortedOptions = computed(() =>
  sortBy(options.value, (option) => textStylePresets.findIndex((i) => i.id === option.id)),
)

const textType = computed(
  () => props.editor.getAttributes('heading').level ?? props.editor.getAttributes('paragraph').size,
)

const setText = (value: Level | Paragraphs) => {
  let textMode: 'heading' | 'paragraph' = 'paragraph'
  if (value === 1 || value === 2 || value === 3) {
    props.editor?.chain().focus().setHeading({ level: value }).run()
    textMode = 'heading'
  } else if (value === Paragraphs.SMALL_TEXT || value === Paragraphs.NORMAL_TEXT || value === Paragraphs.BIG_TEXT) {
    props.editor.chain().focus().setParagraphExtended(value).run()
  }

  const fontSize: string =
    textMode === 'heading'
      ? (headingStyling[value as Level]?.fontSize?.toString() ?? '16')
      : (paragraphStyling[value as Paragraphs]?.fontSize?.toString() ?? '16')

  if (launchDarkly.value.enable_themes || launchDarkly.value.enable_canvas_tokens) return
  props.editor.chain().focus().selectParentNode().setFontSize(fontSize).run()
}
</script>

<style>
.letter-spacing--inherit .n-base-select-option__content {
  letter-spacing: inherit;
}
</style>
